<template>
  <div
    v-if="message"
    class="message__context"
    :class="getClassContext"
    @click="emit('scroll-to-message')"
  >
    <span class="message__context--close" @click="$emit('close')" v-if="closable">
      <CloseOutlined />
    </span>
    <span class="message__context--person">
      <span v-if="message.origin === 'client'">
        {{ message.person ? message.person.full_name :
          message.identifier }}
      </span>
      <span v-else>
        Tú
      </span>
    </span>
    <span
      class="message__context--text"
      v-if="message.type_message === MessageType.File">
      <FilePreview
        :file="{ file: message.media_url, inputType: 'file' }"
        withoutStyles
        workspace
      />
    </span>
    <span
      class="message__context--text"
      v-else-if="message.type_message === MessageType.Image">
      <PictureOutlined />
      Foto
    </span>
    <span
      class="message__context--text"
      v-else-if="message.type_message === MessageType.Sticker">
      <PictureOutlined />
      Sticker
    </span>
    <span
      class="message__context--text"
      v-else-if="message.type_message === MessageType.Video">
      <PlaySquareOutlined />
      Video
    </span>
    <span
      class="message__context--text"
      v-else-if="message.type_message === MessageType.Location">
      <EnvironmentOutlined />
      Ubicación
    </span>
    <span
      class="message__context--text"
      v-else-if="message.type_message === MessageType.Contacts">
      <UserOutlined />
      Contacto: {{ getContact }}
    </span>
    <span
      class="message__context--text"
      v-else-if="message.type_message === MessageType.Audio">
      <AudioOutlined />
      Mensaje de voz ({{ audioDuration }})
    </span>
    <span
      class="message__context--text"
      :class="{ 'message__context--reduced': !showAllContent }"
      v-else
    >
      {{ message?.message }}
    </span>
  </div>
</template>

<script setup>
import { MessageType } from '@/app/shared/utils/enums'
import { EnvironmentOutlined, PictureOutlined, PlaySquareOutlined, UserOutlined, CloseOutlined, AudioOutlined } from '@ant-design/icons-vue'
import { computed, onMounted, ref } from 'vue'
import FilePreview from '../chat/input/FilePreview.vue'

const props = defineProps({
  message: {
    type: Object,
    required: true
  },
  closable: {
    type: Boolean,
    default: false
  },
  showAllContent: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'scrollToMessage'])

const getClassContext = computed(() => {
  const ctx = props.message || {}
  const isAgent = ctx.origin === 'agent' || ctx.created_by_agent
  const isPerson = ctx.origin === 'client'
  return {
    'message__context--agent': isAgent,
    'message__context--client': isPerson
  }
})

const getContact = computed(() => {
  if (props.message?.full_payload?.payload?.payload?.contacts?.length > 0) {
    const contact = props.message.full_payload.payload.payload.contacts[0]
    if (contact)
      return contact.name?.formatted_name || contact.phones?.[0]?.phone || 'Desconocido'
  }
})

const audioDuration = ref(0);

const getAudioDuration = (url) => {
  return new Promise((resolve) => {
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      resolve(audio.duration);
    });
    audio.addEventListener('error', () => {
      resolve(0); // Por si hay error en la carga
    });
  });
};

const formatDuration = (seconds) => {
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

onMounted(async () => {
  if (props.message.media_url && props.message.type_message === MessageType.Audio) {
    const durationInSeconds = await getAudioDuration(props.message.media_url);
    audioDuration.value = formatDuration(durationInSeconds);
  }
})
</script>

<style lang="sass" scoped>
.message__context
  width: calc(100% - 20px)
  border-radius: 5px
  margin: 10px 10px 0 10px
  padding: 5px 10px
  background-color: #99999936
  position: relative
  cursor: pointer

  &--close
    position: absolute
    top: 8px
    right: 8px
    cursor: pointer
    color: #888
    font-size: 16px
    z-index: 1

  &--client
    border-left: 4px solid #2F54EB

    .message__context--person
      color: #2F54EB

  &--agent
    border-left: 4px solid #FA8C16

    .message__context--person
      color: #FA8C16

  &--person
    display: block
    font-weight: 600

  &--text
    display: block
    word-break: break-word
    white-space: pre-wrap
    color: #00000073
    margin-top: 2px

  &--reduced
    display: inline-block
    max-width: 100%
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    vertical-align: middle
</style>
